import React from 'react';
import { i18n } from 'config/locales';
import { FinancialCreditItemModel } from 'forms/sections/financial/credit/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import Field from 'components/Form/Field';
import Telephone from 'components/Form/Telephone';
import Layout from 'components/Form/PVQLocation/Layouts';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { isPVQForm } from 'helpers/form';
import Branch from 'components/Form/Branch';
import PVQLocation from 'components/Form/PVQLocation';

interface Props extends Partial<FinancialCreditItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  dispatch?: Dispatch
  formType: string
  addressBooks?: CommonAddressBooks
}

export default class CreditItem extends ValidationElement<Props> {
  update = (queue: Partial<FinancialCreditItemModel>) => {
    this.props.onUpdate({
      Explanation: this.props.Explanation,
      Name: this.props.Name,
      Telephone: this.props.Telephone,
      InstallationCountry: this.props.InstallationCountry,
      HasBeenEffective: this.props.HasBeenEffective,
      Why: this.props.Why,
      How: this.props.How,
      Location: this.props.Location,
      Description: this.props.Description,
      ...queue,
    });
  };

  updateExplanation = (values: FinancialCreditItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  updateName = (values: FinancialCreditItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateTelephone = (values: FinancialCreditItemModel['Telephone']) => {
    this.update({
      Telephone: values,
    });
  };

  updateLocation = (values: FinancialCreditItemModel['Location']) => {
    this.update({
      Location: values,
    });
  };

  updateDescription = (values: FinancialCreditItemModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateInstallationCountry = (values:FinancialCreditItemModel['InstallationCountry']) => {
    this.update({
      InstallationCountry: values,
    });
  };

  updateHasBeenEffective = (values: FinancialCreditItemModel['HasBeenEffective']) => {
    this.update({
      HasBeenEffective: values,
    });
  };

  updateWhy = (values: FinancialCreditItemModel['Why']) => {
    this.update({
      Why: values,
    });
  };

  updateHow = (values: FinancialCreditItemModel['How']) => {
    this.update({
      How: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);
    return isPVQ ? (
      <div className="credit-item">
        <Field
          attribute={`${attribute}.Name`}
          title={i18n.t('financial.pvq.counseling.branch.ifYes.serviceNameText')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.Name`}
            name="Name"
            {...this.props.Name}
            className="credit-name"
            required={this.props.required}
            onUpdate={this.updateName}
            onError={this.props.onError}
          />
        </Field>
        <Field
          attribute={`${attribute}.Location`}
          title={i18n.t('financial.pvq.counseling.branch.ifYes.branch.ifNo.serviceLocation')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQLocation
            {...this.props.Location}
            name="Location"
            label={i18n.t('financial.pvq.counseling.branch.ifYes.branch.title')}
            domesticTitle={i18n.t('financial.pvq.counseling.branch.ifYes.branch.ifYes.branch.title')}
            foreignTitle={i18n.t('financial.pvq.counseling.branch.ifYes.branch.ifNo.serviceLocation')}
            layout={Location.ADDRESS}
            showMilitaryInstallation

            hideInstallationNameUnknownWhenForeign
            showApoFpoDpoPostcodeWhenForeign
            validated
            className="credit-coundeling-location"
            required={this.props.required}
            onUpdate={this.updateLocation}
            onError={this.props.onError}
            inUSA
            attribute={`${attribute}.Location`}
          />
        </Field>

        <Field
          attribute={`${attribute}.Telephone`}
          title={i18n.t('financial.pvq.counseling.branch.ifYes.servicePhone')}
          className="credit-serphone"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Telephone
            attribute={`${attribute}.Telephone`}
            name="Telephone"
            {...this.props.Telephone}
            className="credit-telephone"
            required={this.props.required}
            allowNotApplicable={false}
            onUpdate={this.updateTelephone}
            onError={this.props.onError}
            showNumberType
          />
        </Field>

        <Branch
          {...this.props.HasBeenEffective}
          label={i18n.t('financial.pvq.counseling.branch.ifYes.effectivenessBranch.title')}
          onUpdate={this.updateHasBeenEffective}
          onError={this.props.onError}
        />
        {(this.props.HasBeenEffective?.value === 'Yes' || this.props.HasBeenEffective?.value === 'No') && (
          <>
            <Field
              attribute={`${attribute}.How`}
              title={i18n.t('financial.pvq.counseling.branch.ifYes.effectivenessBranch.ifYesOrNo.howTextArea')}
              className="credit-service-how"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                attribute={`${attribute}.How`}
                name="how"
                {...this.props.How}
                className="credit-how"
                required={this.props.required}
                allowNotApplicable={false}
                onUpdate={this.updateHow}
                onError={this.props.onError}
              />
            </Field>
            <Field
              attribute={`${attribute}.Why`}
              title={i18n.t('financial.pvq.counseling.branch.ifYes.effectivenessBranch.ifYesOrNo.whyTextArea')}
              className="credit-service-why"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                attribute={`${attribute}.Why`}
                name="why"
                {...this.props.Why}
                className="credit-why"
                required={this.props.required}
                allowNotApplicable={false}
                onUpdate={this.updateWhy}
                onError={this.props.onError}
              />
            </Field>
          </>
        )}
      </div>
    ) : (
      <div className="credit-item">
        <Field
          attribute={`${attribute}.Explanation`}
          title={i18n.t('financial.credit.heading.explanation')}
          scrollIntoView={this.props.scrollIntoView}
          help="financial.credit.help.explanation"
        >
          <Textarea
            attribute={`${attribute}.Explanation`}
            name="Explanation"
            {...this.props.Explanation}
            className="credit-explanation"
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Name`}
          title={i18n.t('financial.credit.heading.name')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.Name`}
            name="Name"
            {...this.props.Name}
            className="credit-name"
            required={this.props.required}
            onUpdate={this.updateName}
            onError={this.props.onError}
          />
        </Field>

        <Field
          attribute={`${attribute}.Telephone`}
          title={i18n.t('financial.credit.heading.telephone')}
          className="override-required"
          help="financial.credit.help.telephone"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="telephone"
        >
          <Telephone
            attribute={`${attribute}.Telephone`}
            name="Telephone"
            {...this.props.Telephone}
            className="credit-telephone"
            required={this.props.required}
            allowNotApplicable={false}
            onUpdate={this.updateTelephone}
            onError={this.props.onError}
          />
        </Field>

        <Field
          attribute={`${attribute}.Location`}
          title={i18n.t('financial.credit.heading.address')}
          help="financial.credit.help.address"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="label"
        >
          <Location
            {...this.props.Location}
            attribute={`${attribute}.Location`}
            name="Location"
            layout={Layout.CITY_STATE}
            className="credit-location"
            dispatch={this.props.dispatch}
            addressBooks={this.props.addressBooks}
            addressBook="Agency"
            bind
            help=""
            required={this.props.required}
            onUpdate={this.updateLocation}
            onError={this.props.onError}
          />
        </Field>

        <Field
          attribute={`${attribute}.Description`}
          title={i18n.t('financial.credit.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
          help="financial.credit.help.description"
        >
          <Textarea
            attribute={`${attribute}.Description`}
            name="Description"
            {...this.props.Description}
            className="credit-description"
            required={this.props.required}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
          />
        </Field>
      </div>
    );
  }
}

CreditItem.defaultProps = {
  onUpdate: (queue) => {},
  onError: (value, arr) => arr,
  required: true,
};
